import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { memberSignIn, fetchCompany, fetchMemberByEmail } from "../../api/memberApi";
import { useAuth } from "../../providers/AuthProvider";
import { resolveBackendErrorKey } from "../../utils/backendErrors";

export default function SignInPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { login } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({
    companyDomainName: "",
    companyPassword: "",
    memberAddress: "",
    memberPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // OK2: 複数メンバーが返ってきた場合の状態
  const [selectMode, setSelectMode] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [signInResult, setSignInResult] = useState(null);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrorMsg("");
  };

  // OK1 / OK2 共通のログイン完了処理
  const completeLogin = async (companyId, email) => {
    const [company, member] = await Promise.all([
      fetchCompany(companyId),
      fetchMemberByEmail(email, companyId, form.memberPassword),
    ]);
    login(companyId, member, company, email);
    navigate("/", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await memberSignIn(form);

      if (res.result === "OK1") {
        // 1名マッチ → そのままログイン
        await completeLogin(res.returnValue, res.loginAddress);
      } else if (res.result === "OK2") {
        // 複数名マッチ → 選択画面を表示
        setSignInResult(res);
        setCandidates(res.members || []);
        setSelectMode(true);
      }
    } catch (err) {
      setErrorMsg(t(resolveBackendErrorKey(err.message, "auth.error.signin")));
    } finally {
      setLoading(false);
    }
  };

  // OK2: メンバー選択後のログイン（選択済みメンバーを直接使用、再フェッチしない）
  const handleSelectMember = async (selectedMember) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const company = await fetchCompany(signInResult.returnValue);
      const email = selectedMember.email || selectedMember.memberAddress || form.memberAddress;
      login(signInResult.returnValue, selectedMember, company, email);
      navigate("/", { replace: true });
    } catch (err) {
      setErrorMsg(t(resolveBackendErrorKey(err.message, "auth.error.signin")));
    } finally {
      setLoading(false);
    }
  };

  // ── 複数メンバー選択画面 ──
  if (selectMode) {
    return (
      <Container maxWidth="xs">
        <Box sx={{ mt: 10, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography component="h1" variant="h5" gutterBottom>
            {t("auth.selectMember.title")}
          </Typography>
          <Paper sx={{ p: 3, width: "100%", mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("auth.selectMember.description")}
            </Typography>
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg}
              </Alert>
            )}
            <List disablePadding>
              {candidates.map((m, index) => (
                <React.Fragment key={m.memberId || m.email || index}>
                  {index > 0 && <Divider />}
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectMember(m)}
                      disabled={loading}
                    >
                      <ListItemText
                        primary={m.fullName || `${m.firstName || ""} ${m.lastName || ""}`.trim()}
                        secondary={m.email || m.memberAddress}
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      </Container>
    );
  }

  // ── サインインフォーム ──
  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "secondary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <LockOutlinedIcon sx={{ color: "white" }} />
        </Box>
        <Typography component="h1" variant="h5" gutterBottom>
          {t("auth.signin.title")}
        </Typography>

        <Paper sx={{ p: 4, width: "100%", mt: 2 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label={t("auth.company.domainName")}
              name="companyDomainName"
              value={form.companyDomainName}
              onChange={handleChange}
              fullWidth
              required
              autoFocus
              margin="normal"
            />
            <TextField
              label={t("auth.company.password")}
              name="companyPassword"
              type="password"
              value={form.companyPassword}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
            <TextField
              label={t("auth.member.email")}
              name="memberAddress"
              type="email"
              value={form.memberAddress}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
            <TextField
              label={t("auth.member.password")}
              name="memberPassword"
              type="password"
              value={form.memberPassword}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {t("auth.signin.title")}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
